import { memo, useCallback, useMemo } from 'react';

import * as PickPairStore from '@Stores/PickPair';
import * as ModalWindows from '@Stores/ModalWindows';
import * as PageProductStore from '@Stores/PageProduct';
import SmartRender from '@Components/SmartRender';
import CreditTab from '@Components/CreditTab';
import YandexSplitCreditTab from '@Components/YandexSplitCreditTab';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import useTranslation from '@Queries/useTranslation';
import useSearchParams from '@Hooks/useSearchParams';
import VerticalBadge from './icons/Vertical.svg';

import type { CreditData, ProductData } from '@Types/Product';
import type { CountryCode } from '@Types/Base';

interface Props {
  pageCredit: CreditData;
  product: ProductData;
  index?: number;
  className?: string;
}

const CreditTabWrapper = (props: Props) => {
  const { pageCredit, product, index, ...restProps } = props;
  const { t } = useTranslation();
  const { creditAvailable, partialPaymentAvailable } = useFeatureFlags();
  const shopProducts = PickPairStore.useSelectedSet();
  const searchParams = useSearchParams();

  const searchParamsForCredit = useMemo(() => {
    const newParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (key.startsWith('products') || key.startsWith('additional')) {
        newParams.append(key, value);
      }
    });

    return newParams.toString();
  }, [searchParams]);

  const isCreditAvailable = creditAvailable
    ? pageCredit.creditAvailable
    : pageCredit.yandexSplitAvailable;
  const isPartialAvailable = partialPaymentAvailable && pageCredit.halvaPartialAvailable;

  const isBankInstallment =
    pageCredit.creditAvailable ||
    pageCredit.tinkoffInstallmentAvailable ||
    pageCredit.yandexSplitAvailable;

  const countries = useMemo<CountryCode[]>(() => {
    if (pageCredit.creditAvailable) return ['RUS', 'KAZ', 'BLR', 'UZB'];
    if (
      pageCredit.halvaPartialAvailable ||
      pageCredit.tinkoffInstallmentAvailable ||
      pageCredit.yandexSplitAvailable
    )
      return ['RUS'];
    return [];
  }, [pageCredit]);

  const handleClickCredit = useCallback(() => {
    ModalWindows.open('BuyInCredit', {
      product,
      productId: product.id,
      title: pageCredit.title,
      shopProducts,
      parameterValues: PageProductStore.getParameterValues(),
      tabId: pageCredit.id,
      index,
      price: product.price.actual,
      searchParams: searchParamsForCredit,
      promoTag: pageCredit.promoTag,
    });
  }, [pageCredit, product, index, shopProducts, searchParamsForCredit]);

  return (
    <SmartRender
      countries={countries}
      condition={(isCreditAvailable && !product.isPreorder) || isPartialAvailable}
    >
      {pageCredit.yandexSplitAvailable ? (
        <>
          <YandexSplitCreditTab
            amount={pageCredit.price}
            onClick={handleClickCredit}
            promoTag={pageCredit.promoTag}
            {...restProps}
          />

          {product.price.actual <= 70000 && pageCredit.promoTag && (
            <img src={VerticalBadge} alt='Vertical Badge' />
          )}
        </>
      ) : (
        <CreditTab
          onClick={handleClickCredit}
          credit={pageCredit}
          text={isBankInstallment ? t('installment-from') : t('installment')}
          textEnd={isBankInstallment ? `/${t('month')}` : ' x 4'}
          showIcon={false}
          {...restProps}
        />
      )}
    </SmartRender>
  );
};

export default memo(CreditTabWrapper);
